/**
 * 模組頁面代碼: 1210-UI
 * 模組說明: 員工資料頁面
 * @module EmployeeContainer
 */

// ----- import from local libraries -----
import {React} from '../../components/BaseComponent';

// ----- import from local components -----
import Header from '../../features/main-frame/components/Header';
import Footer from '../../features/main-frame/components/Footer';
import Sidebar from '../../features/main-frame/components/Sidebar';
import {useSelector} from 'react-redux';
import enumList from 'web/utils/enumList';
import {useRef, useState, useEffect} from 'react';
import {AlertModal} from 'web/components/modal/AlertModal';
import EmployeePage from './components/EmployeePage';
import {editEmployeeStatus} from 'web/redux/actions/EmployeeActions';
import {showMessage} from 'web/utils/modalHelper';
import {useDispatch} from 'react-redux';
import DepartmentListModal from '../department/components/DepartmentListModal';
import ConditionModal from '../condition/components/ConditionModal';

import {
  fetchDepartmentList,
  editDepartmentStatus,
} from 'web/redux/actions/DepartmentActions';
import {fetchPositionList} from 'web/redux/actions/PositionActions';
import {editPositionStatus} from 'web/redux/actions/PositionActions';
import EmployeeModal from './components/EmployeeModal';
import DepartmentModel from '../department/components/DepartmentModal';
import PositionListModal from '../position/components/PositionListModal';
import PositionModal from '../position/components/PositionModal';
import {fetchConditionSelection} from 'web/redux/actions/ConditionActions';
import ConditionListModal from '../condition/components/ConditionListModal';
import {fetchConditionList} from 'web/redux/actions/ConditionActions';

import {useConditionListModalControl} from '../condition/hooks/useConditionListModalControl';
import {useConditionModalControl} from '../condition/hooks/useConditionModalControl';
import {useAlertModal} from 'web/components/modal/hooks/useAlertModal';
import {useDepartmentListModalControl} from '../department/hooks/useDepartmentListModalControl';
import {usePositionListModalControl} from '../position/hooks/usePositionListModalControl';

import {editConditionStatus} from 'web/redux/actions/ConditionActions';
import {usePositionModalControl} from '../position/hooks/usePositionModalControl';
import {useDepartmentModelControl} from '../department/hooks/useDepartmentControl';

// ----- constants and enums -----

const EmployeeContainer = (props) => {
  const dispatch = useDispatch();
  const userPermissions = useSelector((state) =>
    JSON.parse(state.access.userPermissions),
  );
  const [needEmployeeLoad, setNeedEmployeeLoad] = useState(false);

  const employeeModalRef = useRef(null);

  const [employeeModalMode, setEmployeeModalMode] = useState(
    enumList.modalMode.new,
  );
  const [needSearchLoad, setNeedSearchLoad] = useState(false);
  const [needDispatchListLoad, setNeedDispatchListLoad] = useState(false);

  const [departmentSelection, setDepartmentSelection] = useState([]);
  const [positionSelection, setPositionSelection] = useState([]);
  const [conditionSelection, setConditionSelection] = useState([]);

  const [employeeItem, setEmployeeItem] = useState({
    employeeCode: '',
    employeeName: '',
    positionCode: '',
    departmentName: '',
    positionName: '',
    email: '',
    conditions: [],
    areaID: null,
  });

  const {
    conditionList,
    dispatchModalRef,
    getConditionList,
    toggleDispatchListModal,
  } = useConditionListModalControl(setNeedDispatchListLoad);

  const {conditionModalMode, conditionModalRef, toggleConditionModal} =
    useConditionModalControl();
  const {
    toggleAlertModal,
    alertRef,
    alertMessage,
    actionIds,
    actionType,
    actionMode,
    setActionType,
    setActionMode,
    setActionIds,
  } = useAlertModal();

  const {
    departmentList,
    departmentListModalRef,
    toggleDepartmentListModel,
    getDepartmentList,
    needDepartmentListLoad,
    setNeedDepartmentListLoad,
  } = useDepartmentListModalControl(setNeedSearchLoad);

  const {departmentModelRef, toggleDepartmentModel, departmentModelMode} =
    useDepartmentModelControl(needDepartmentListLoad);

  const {
    positionList,
    positionListModalRef,
    togglePositionListModal,
    getPositionList,
    needPositionListLoad,
    setNeedPositionListLoad,
  } = usePositionListModalControl();

  const {positionModalMode, positionModalRef, togglePositionModal} =
    usePositionModalControl();

  const setEmployeeModalRef = (element) => {
    if (element && !employeeModalRef.current) {
      employeeModalRef.current = new bootstrap.Modal(element);
    }
  };

  const toggleEmployeeAddModal = (isShow) => {
    setEmployeeItem({
      employeeCode: '',
      employeeName: '',
      departmentName: '',
      positionName: '',
      email: '',
      conditions: [],
      areaID: null,
    });
    if (employeeModalRef) {
      if (isShow) {
        setEmployeeModalMode(enumList.modalMode.new);
        employeeModalRef.current.show();
      } else {
        employeeModalRef.current.hide();
      }
    }
  };

  const toggleEmployeeEditModal = (isShow, employeeItem) => {
    if (employeeModalRef) {
      setEmployeeItem(employeeItem);
      if (isShow) {
        setEmployeeModalMode(enumList.modalMode.edit);
        employeeModalRef.current.show();
      } else {
        employeeModalRef.current.hide();
      }
    }
  };

  const handleEditDepartmentStatus = (departmentCodes, status) => {
    dispatch(editDepartmentStatus({departmentCodes, flgStatus: status}))
      .then((res) => {
        const statusCode = res.payload.code;
        if (statusCode && statusCode.isSuccessCode()) {
          toggleAlertModal(false);
          getDepartmentList(true);
        } else {
          showMessage(enumList.messageModalMode.error, res.payload.message);
          toggleAlertModal(false);
        }
      })
      .catch((err) => {
        showMessage(enumList.messageModalMode.error, '');
        console.error('updateDepartmentStatus error', err);
      });
  };

  const handleEditEmployeeStatus = (employeeCodes, status) => {
    dispatch(editEmployeeStatus({employeeCodes, status}))
      .then((res) => {
        const statusCode = res.payload.code;
        if (statusCode && statusCode.isSuccessCode()) {
          setNeedEmployeeLoad(true);
          toggleAlertModal(false);
        } else {
          showMessage(enumList.messageModalMode.error, res);
          toggleAlertModal(false);
        }
      })
      .catch((err) => {
        showMessage(enumList.messageModalMode.error, '', err);
        console.error('updateCustomerStatus error', err);
      });
  };

  const handleEditPositionStatus = (positionCodes, status) => {
    dispatch(editPositionStatus({positionCode: positionCodes[0], status}))
      .then((res) => {
        const statusCode = res.payload.code;
        if (statusCode && statusCode.isSuccessCode()) {
          toggleAlertModal(false);
          setTimeout(() => {
            getPositionList(true);
          }, 100);
          // getPositionList(true);
        } else {
          showMessage(enumList.messageModalMode.error, res.payload.message);
          toggleAlertModal(false);
        }
      })
      .catch((err) => {
        showMessage(enumList.messageModalMode.error, '');
        console.error('updatePositionStatus error', err);
      });
  };

  const handleEditConditionStatus = (conditionCodes, mode) => {
    dispatch(editConditionStatus({conditionCodes, flgStatus: mode}))
      .then((res) => {
        const statusCode = res.payload.code;
        if (statusCode && statusCode.isSuccessCode) {
          toggleAlertModal(false);
          getConditionList(true);
          // setTimeout(() => {
          //   getConditionList(true);
          // }, 100);
        } else {
          console.error('fetchEditConditionStatus error', res);
        }
      })
      .catch((err) => {
        showErrorMessage(enumList.messageModalMode.error, err);
        console.error('fetchEditConditionStatus error', err);
      });
  };

  const onConfirm = () => {
    const status =
      actionMode === enumList.alertModalModeEnums.enable ?
        enumList.flgStatus.active :
        enumList.flgStatus.inactive;
    if (actionType === enumList.targetAction.employee) {
      handleEditEmployeeStatus(actionIds, status);
    } else if (actionType === enumList.targetAction.department) {
      handleEditDepartmentStatus(actionIds, status);
    } else if (actionType === enumList.targetAction.position) {
      handleEditPositionStatus(actionIds, status);
    } else if (actionType === enumList.targetAction.condition) {
      handleEditConditionStatus(actionIds, status);
    }
  };

  useEffect(() => {
    getDepartmentList();
    getPositionList();
    getConditionList();
  }, []);

  return (
    <>
      <div className="m__wrap">
        <Header />
        <div className="m__contents">
          <Sidebar />
          <EmployeePage
            {...props}
            userPermissions={userPermissions}
            onBatchHandle={(mode, type, message, actionCodes) =>
              toggleAlertModal(true, mode, type, message, actionCodes)
            }
            toggleEmployeeAddModal={() => toggleEmployeeAddModal(true)}
            toggleEmployeeEditModal={(show, employeeItem) =>
              toggleEmployeeEditModal(show, employeeItem)
            }
            needEmployeeLoad={needEmployeeLoad}
            setNeedEmployeeLoad={setNeedEmployeeLoad}
            onDepartmentListModal={() => toggleDepartmentListModel(true)}
            onPositionListModal={() => togglePositionListModal(true)}
            onDispatchListModal={() => toggleDispatchListModal(true)}
            needSearchLoad={needSearchLoad}
            setNeedSearchLoad={setNeedSearchLoad}
            onDepartmentSelection={(departmentSelection) =>
              setDepartmentSelection(departmentSelection)
            }
            onPositionSelection={(positionSelection) =>
              setPositionSelection(positionSelection)
            }
            positionList={positionList}
            departmentList={departmentList}
          />
        </div>
        <Footer />
      </div>
      <AlertModal
        ref={alertRef}
        mode={actionMode}
        message={alertMessage}
        onConfirm={() => onConfirm()}
        onCancel={() => setNeedEmployeeLoad(true)}
      />
      <DepartmentModel
        {...props}
        ref={departmentModelRef}
        checkPermission={userPermissions}
        mode={departmentModelMode}
        setNeedEmployeeLoad={setNeedEmployeeLoad}
        needDepartmentListLoad={needDepartmentListLoad}
        setNeedDepartmentListLoad={(bLoad) => setNeedDepartmentListLoad(bLoad)}
        onDepartment={() => {
          toggleDepartmentModel(false);
          // toggleDepartmentListModel(true);
        }}
        reloadDepartmentList={() => {
          getDepartmentList(true);
        }}
      />
      <DepartmentListModal
        {...props}
        ref={departmentListModalRef}
        checkPermission={userPermissions}
        departmentList={departmentList}
        needDepartmentListLoad={needDepartmentListLoad}
        setNeedDepartmentListLoad={(bLoad) => setNeedDepartmentListLoad(bLoad)}
        onDepartment={(mode = enumList.modalMode.new, code) => {
          toggleDepartmentListModel(false);
          toggleDepartmentModel(true, mode, code);
        }}
        onEditDepartment={(index) => {
          // this.toggleRoleModal(true, RoleModalModeEnums.EDIT, index);
          // this.toggleRoleListModal(false);
        }}
        onEditDepartmentStatus={(mode, message, departmentCode) => {
          toggleAlertModal(
            true,
            mode,
            enumList.targetAction.department,
            message,
            [departmentCode],
          );
          toggleDepartmentListModel(false);
        }}
      />
      <PositionListModal
        {...props}
        ref={positionListModalRef}
        checkPermission={userPermissions}
        positionList={positionList}
        needPositionListLoad={needPositionListLoad}
        setNeedPositionListLoad={(bLoad) => setNeedPositionListLoad(bLoad)}
        onEditPositionStatus={(mode, message, positionCode) => {
          toggleAlertModal(
            true,
            mode,
            enumList.targetAction.position,
            message,
            [positionCode],
          );
          togglePositionListModal(false);
        }}
        onPosition={(mode = enumList.modalMode.new, positionId) => {
          togglePositionModal(true, mode, positionId);
          togglePositionListModal(false);
        }}
      />
      <PositionModal
        {...props}
        ref={positionModalRef}
        checkPermission={userPermissions}
        mode={positionModalMode}
        setNeedEmployeeLoad={setNeedEmployeeLoad}
        needPositionListLoad={needPositionListLoad}
        setNeedPositionListLoad={(bLoad) => setNeedPositionListLoad(bLoad)}
        onPosition={() => {
          togglePositionModal(false);
          togglePositionListModal(true);
        }}
        reloadPositionList={() => {
          getPositionList(true);
        }}
      />
      <EmployeeModal
        {...props}
        userPermissions={userPermissions}
        ref={setEmployeeModalRef}
        mode={employeeModalMode}
        employeeItem={employeeItem}
        conditionSelection={conditionList.filter(
          ({flgStatus}) => flgStatus !== enumList.flgStatus.inactive,
        )}
        departmentSelection={departmentList.filter(
          ({flgStatus}) => flgStatus !== enumList.flgStatus.inactive,
        )}
        positionSelection={positionList.filter(
          ({flgStatus}) => flgStatus !== enumList.flgStatus.inactive,
        )}
        toggleEmployeeEditModal={toggleEmployeeEditModal}
        toggleEmployeeAddModal={(show) => {
          toggleEmployeeAddModal(show);
        }}
        reloadEmployeeList={() => {
          setNeedEmployeeLoad(true);
        }}
        onErrorHandle={(title, message) => showMessage(true, message)}
      />
      <ConditionListModal
        {...props}
        ref={dispatchModalRef}
        conditionList={conditionList}
        needDispatchListLoad={needDispatchListLoad}
        setNeedDispatchListLoad={(bLoad) => setNeedDispatchListLoad(bLoad)}
        onEditConditionStatus={(mode, message, conditionCode) => {
          toggleAlertModal(
            true,
            mode,
            enumList.targetAction.condition,
            message,
            [conditionCode],
          );
          toggleDispatchListModal(false);
        }}
        onCondition={(mode = enumList.modalMode.new, conditionCode) => {
          toggleConditionModal(true, mode, conditionCode);
          toggleDispatchListModal(false);
        }}
      />
      <ConditionModal
        {...props}
        ref={conditionModalRef}
        mode={conditionModalMode}
        setNeedEmployeeLoad={setNeedEmployeeLoad}
        onAddCondition={() => {
          getConditionList(true);
          toggleConditionModal(false);
        }}
      />
    </>
  );
};

export default EmployeeContainer;
